import classNames from 'classnames';
import * as React from 'react';

import { Shape2, Shape2Icon, Shape2Theme, Shape2Tone } from '../../components/Shape2/Shape2';
import { Bem } from '../../service/Bem/Bem';
import { CanvasEditableLabelContainer } from '../CanvasEditableLabel/CanvasEditableLabelContainer';

import { CreativeOptionGroup } from './CreativeOptionGroup/CreativeOptionGroup';
import './CreativeOptions.css';
import { optionGroupVisible } from './helpers';
import {
    CommonOptionGroupName,
    CommonOptionName,
    CreativeOptionsConfig,
    CreativeOptionsErrors,
    CreativeOptionsGroupValue,
    CreativeOptionsValue,
    CreativeOptionValue
} from './typings';

export interface CreativeOptionsProps {
    cls?: string;
    name: string;
    onSaveName: (name: string) => void;

    optionsConfig: CreativeOptionsConfig;
    optionsValue: CreativeOptionsValue;
    errors?: CreativeOptionsErrors;
    tooltipsVisible?: boolean;
    isDocCenterTooltip?: boolean;

    onOptionChange: (optionGroupName: string, optionName: string, value: string) => void;
    onGroupVisibleChange: (groupName: string) => void;
}

interface CreativeOptionsState {
    isScrolling?: number;
    tooltipVisible: boolean;
}

const BLOCK = 'creative-options';
const CREATIVE_OPTIONS_EDITOR_ID = 'creative-options';

export class CreativeOptions extends React.PureComponent<CreativeOptionsProps, CreativeOptionsState> {
    public static defaultProps = {
        tooltipsVisible: true
    };

    public state: CreativeOptionsState = {
        tooltipVisible: true
    };

    private options: React.RefObject<HTMLDivElement> = React.createRef();

    componentDidMount(): void {
        if (this.options.current) {
            this.options.current.addEventListener('scroll', this.onListScroll);
        }
    }

    componentWillMount(): void {
        if (this.options.current) {
            this.options.current.removeEventListener('scroll', this.onListScroll);
        }
    }

    componentWillReceiveProps(nextProps: Readonly<CreativeOptionsProps>): void {
        if (this.props.tooltipsVisible !== nextProps.tooltipsVisible) {
            if (nextProps.tooltipsVisible) {
                window.setTimeout(() => {
                    this.setState({
                        tooltipVisible: true
                    });
                }, 200);
            } else {
                this.setState({
                    tooltipVisible: false
                });
            }
        }
    }

    render(): React.ReactNode {
        const { cls, name, onSaveName } = this.props;

        return (
            <div className={classNames(BLOCK, cls)}>
                <div className={Bem.cls(BLOCK, 'title-wrapper')}>
                    <CanvasEditableLabelContainer
                        savedValue={name}
                        onValueSaved={onSaveName}
                        editorId={CREATIVE_OPTIONS_EDITOR_ID}
                        hasClear={false}
                        enableEmptyInput={false}
                        withControls={false}
                        editBtnCls={Bem.cls(BLOCK, 'edit-btn')}
                        cls={Bem.cls(BLOCK, 'title')}
                        iconRight={
                            <Shape2
                                width={10}
                                height={10}
                                icon={Shape2Icon.enter}
                                theme={Shape2Theme.light}
                                tone={Shape2Tone.grey}
                            />
                        }
                    />
                </div>
                <div className={Bem.cls(BLOCK, 'list-wrapper')} ref={this.options}>
                    <div className={Bem.cls(BLOCK, 'list')}>{this.renderOptionsGroups()}</div>
                </div>
            </div>
        );
    }

    private onListScroll = (): void => {
        const { isScrolling } = this.state;

        clearTimeout(isScrolling);
        const newIsScrolling = window.setTimeout(() => {
            this.setState({
                tooltipVisible: true
            });
        }, 250);

        this.setState({
            tooltipVisible: false,
            isScrolling: newIsScrolling
        });
    };

    private getValues = (value: CreativeOptionsGroupValue): Record<string, CreativeOptionValue> => {
        switch (value.type) {
            case CommonOptionGroupName.logo:
            case CommonOptionGroupName.image:
                return {
                    [CommonOptionName.fileId]: value.options[CommonOptionName.fileId]
                };

            default:
                return value.options;
        }
    };

    private renderOptionsGroups(): React.ReactNode {
        const {
            optionsConfig,
            optionsValue,
            errors,
            onOptionChange,
            onGroupVisibleChange,
            isDocCenterTooltip
        } = this.props;
        const { tooltipVisible } = this.state;

        return optionsValue.map((value: CreativeOptionsGroupValue) => {
            const groupData = optionsConfig[value.type];
            if (!groupData) {
                return null;
            }

            const { title, canBeHidden, tooltip, options } = groupData;

            if (!optionGroupVisible(options, value.options)) {
                return null;
            }

            switch (value.type) {
                case CommonOptionGroupName.image:
                case CommonOptionGroupName.logo:
                    break;

                default:
                    if (value.externalHidden) {
                        return null;
                    }

                    break;
            }

            return (
                <CreativeOptionGroup
                    cls={Bem.cls(BLOCK, 'group')}
                    key={value.type}
                    title={title}
                    name={value.type}
                    tooltip={tooltip}
                    options={options}
                    values={this.getValues(value)}
                    canBeHidden={canBeHidden}
                    visible={value.visible}
                    errors={errors ? errors[value.type] : undefined}
                    onVisibleChange={onGroupVisibleChange}
                    onOptionChange={onOptionChange}
                    tooltipsVisible={tooltipVisible}
                    isDocCenterTooltip={isDocCenterTooltip}
                />
            );
        });
    }
}
